import { useAppSelector } from '../useReduxToolkit';
import { getIsGalleryView } from '../../features/call/call.selectors';
import { getSessionParticipantsIds } from '../../features/session/session.selectors';
import { useCallback, useEffect, useRef } from 'react';

export enum CallViewTimeName {
  GALLERY = 'galleryViewDuration',
  SPEAKER = 'speakerViewDuration',
}

type CallViewTimes = Record<CallViewTimeName, number>;

type UseCallViewTimeReturnType = () => CallViewTimes;

type CurrentTimer = {
  name: CallViewTimeName;
  startTime: number;
};

const updateViewTime = (
  viewTime: CallViewTimes,
  currentTimer: CurrentTimer | null,
): void => {
  const now = Date.now();

  if (currentTimer !== null) {
    const timeInView = now - currentTimer.startTime;

    currentTimer.startTime = now;

    viewTime[currentTimer.name] += timeInView;
  }
};

export const useCallViewTime = (): UseCallViewTimeReturnType => {
  const isGroupCall = useAppSelector(getSessionParticipantsIds).length > 1;
  const isGalleryView = useAppSelector(getIsGalleryView);

  const viewTimeRef = useRef<Record<CallViewTimeName, number>>({
    [CallViewTimeName.GALLERY]: 0,
    [CallViewTimeName.SPEAKER]: 0,
  });
  const currentTimerRef = useRef<CurrentTimer | null>(null);

  const switchTimer = useCallback(
    (timerName: CallViewTimeName | null): void => {
      if (timerName === (currentTimerRef.current?.name ?? null)) {
        return;
      }
      updateViewTime(viewTimeRef.current, currentTimerRef.current);

      currentTimerRef.current =
        timerName === null
          ? null
          : {
              name: timerName,
              startTime: Date.now(),
            };
    },
    [],
  );

  useEffect(() => {
    if (isGroupCall) {
      switchTimer(
        isGalleryView ? CallViewTimeName.GALLERY : CallViewTimeName.SPEAKER,
      );
    } else {
      switchTimer(null);
    }
  }, [isGroupCall, isGalleryView, switchTimer]);

  return useCallback(() => {
    updateViewTime(viewTimeRef.current, currentTimerRef.current);
    return viewTimeRef.current;
  }, []);
};
